/**
 * Itineraries tab - the review-and-assign surface for Smart Schedule
 * output. Built for volume: a generation run over a big pool can draft
 * dozens of trips, so this is a filterable DataTable (not a card wall).
 * Drafts are UNDATED - dates appear once a coordinator assigns inspector
 * + start date (available right here per row, and on the detail page).
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router'
import { CalendarPlus, MapPin, Route, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import api from '@/admin/lib/api'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { Badge } from '@/shared/components/ui/badge'
import { formatDistanceMeters } from '@/shared/lib/formatting'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { toastApiError } from '@/shared/lib/api-error'
import {
  useCancelItinerary,
  useInspections,
  useItineraries,
  type ItinerarySummary,
  type ItineraryStatus,
} from '@/admin/hooks/use-inspections'
import { AssignItineraryDialog } from './assign-itinerary-dialog'

const STATUS_TONE: Record<
  ItineraryStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  scheduled: 'default',
  cancelled: 'outline',
}

function formatDrive(
  seconds: number | null,
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
): string {
  if (!seconds) return '-'
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  if (h) {
    return m
      ? t('inspections.itineraries.driveHoursMinutes', '{{hours}} h {{minutes}} m', {
          hours: h,
          minutes: m,
        })
      : t('inspections.itineraries.driveHours', '{{hours}} h', { hours: h })
  }
  return t('inspections.itineraries.driveMinutes', '{{minutes}} m', { minutes: m })
}

export function ItinerariesTab({
  onSmartSchedule,
  canSchedule,
}: {
  onSmartSchedule: () => void
  canSchedule: boolean
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { locale } = useFormatting()
  const [statusFilter, setStatusFilter] = useState<ItineraryStatus | 'all'>(
    'all',
  )
  const { data, isLoading, isFetching } = useItineraries(statusFilter)
  const cancelMutation = useCancelItinerary()
  const [assigning, setAssigning] = useState<ItinerarySummary | null>(null)
  const [backlogOpen, setBacklogOpen] = useState(false)

  const rows = useMemo(() => data?.results ?? [], [data])

  // The "awaiting itinerary" backlog: staged drafts not on any live trip.
  const { data: backlog } = useInspections({
    staged: 'true',
    unrouted: 'true',
  })
  const awaitingCount = backlog?.length ?? null

  // Un-stage a backlog home: cancel the draft inspection through the
  // engine's cancel action - the home returns to pool eligibility.
  const qc = useQueryClient()
  const unstage = useMutation({
    mutationFn: async (inspectionUuid: string) => {
      const res = await api.post(`/inspections/${inspectionUuid}/cancel/`, {
        reason_code: 'unstaged',
        notes: 'Removed from the staged backlog on the Itineraries tab.',
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inspections'] })
      qc.invalidateQueries({ queryKey: ['contractor-summary'] })
    },
  })

  const columns: DataTableColumn<ItinerarySummary>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('inspections.itineraries.columnTrip', 'Trip'),
        accessorKey: 'name',
        cell: (row) => (
          <span className="flex flex-col">
            <span className="font-medium text-[var(--ink)]">{row.name}</span>
            <span className="text-[10px] text-[var(--ink-3)]">
              {t('inspections.itineraries.perStopMinutes', '{{minutes}} m/stop', {
                minutes: row.per_stop_minutes,
              })}
              {' · '}
              {t('inspections.itineraries.maxDayHours', '{{hours}} h days', {
                hours: Math.round(row.max_day_minutes / 60),
              })}
              {row.origin_address
                ? t('inspections.itineraries.tripOriginSuffix', ' · from {{address}}', {
                    address: row.origin_address,
                  })
                : ''}
            </span>
          </span>
        ),
      },
      {
        id: 'stops',
        header: t('inspections.itineraries.columnStops', 'Stops'),
        accessorKey: 'stop_count',
        cell: (row) => row.stop_count,
      },
      {
        id: 'days',
        header: t('inspections.itineraries.columnDays', 'Days'),
        accessorKey: 'day_count',
        cell: (row) => row.day_count,
      },
      {
        id: 'distance',
        header: t('inspections.itineraries.columnDistance', 'Distance'),
        cell: (row) =>
          row.total_distance_meters
            ? formatDistanceMeters(row.total_distance_meters, locale)
            : '-',
      },
      {
        id: 'drive',
        header: t('inspections.itineraries.columnDriveEst', 'Drive (est.)'),
        cell: (row) => formatDrive(row.total_duration_seconds, t),
      },
      {
        id: 'status',
        header: t('inspections.itineraries.columnStatus', 'Status'),
        accessorKey: 'status',
        cell: (row) => (
          <Badge variant={STATUS_TONE[row.status] ?? 'outline'}>
            {row.status}
          </Badge>
        ),
      },
      {
        id: 'schedule',
        header: t('inspections.itineraries.columnSchedule', 'Schedule'),
        cell: (row) =>
          row.status === 'scheduled' && row.start_date ? (
            <span className="flex flex-col text-xs">
              <span>{row.inspector_name || '-'}</span>
              <span className="text-[10px] text-[var(--ink-3)]">
                {t('inspections.itineraries.scheduleFrom', 'from {{date}}', {
                  date: row.start_date,
                })}
              </span>
            </span>
          ) : (
            <span className="text-[var(--ink-3)]">
              {t('inspections.itineraries.notScheduledYet', 'not scheduled yet')}
            </span>
          ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) =>
          row.status === 'draft' && canSchedule ? (
            <span
              className="flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="xs"
                variant="outline"
                onClick={() => setAssigning(row)}
              >
                <CalendarPlus size={12} className="mr-1" />
                {t('inspections.itineraries.assign', 'Assign')}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                onClick={async () => {
                  try {
                    await cancelMutation.mutateAsync(row.uuid)
                    toast.success(
                      t(
                        'inspections.itineraries.discardSuccess',
                        'Draft discarded - stops returned to the pool.',
                      ),
                    )
                  } catch (e) {
                    toastApiError(
                      e,
                      t('inspections.itineraries.discardError', 'Could not discard this draft.'),
                    )
                  }
                }}
              >
                {t('inspections.itineraries.discard', 'Discard')}
              </Button>
            </span>
          ) : null,
      },
    ],
    [locale, canSchedule, cancelMutation, t],
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v: string) =>
              setStatusFilter(v as ItineraryStatus | 'all')
            }
          >
            <SelectTrigger className="h-9 w-[150px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('inspections.itineraries.statusAll', 'All statuses')}
              </SelectItem>
              <SelectItem value="draft">
                {t('inspections.itineraries.statusDraft', 'Draft')}
              </SelectItem>
              <SelectItem value="scheduled">
                {t('inspections.itineraries.statusScheduled', 'Scheduled')}
              </SelectItem>
              <SelectItem value="cancelled">
                {t('inspections.itineraries.statusCancelled', 'Cancelled')}
              </SelectItem>
            </SelectContent>
          </Select>
          {awaitingCount != null && awaitingCount > 0 && (
            <Button
              variant="outline"
              size="xs"
              onClick={() => setBacklogOpen(true)}
              className="rounded-full text-[var(--ink-2)]"
              title={t(
                'inspections.itineraries.backlogButtonTitle',
                'Staged homes not yet routed onto any trip - click to see them',
              )}
            >
              {t('inspections.itineraries.awaitingCount', '{{count}} staged, awaiting itinerary', {
                count: awaitingCount,
              })}
            </Button>
          )}
        </div>
        {canSchedule && (
          <Button
            onClick={onSmartSchedule}
            className="bg-[var(--green,#1c5a48)] text-white hover:bg-[var(--green,#1c5a48)]/90"
          >
            <Route size={14} className="mr-1.5" />
            {t('inspections.itineraries.smartSchedule', 'Smart Schedule')}
          </Button>
        )}
      </div>

      {!isLoading && !isFetching && rows.length === 0 ? (
        <EmptyState
          icon={Route}
          title={t('inspections.itineraries.emptyTitle', 'No itineraries yet')}
          description={t(
            'inspections.itineraries.emptyDescription',
            'Run Smart Schedule on the Pool tab to draft regional trips from everything that is due.',
          )}
          action={
            canSchedule
              ? {
                  label: t('inspections.itineraries.smartSchedule', 'Smart Schedule'),
                  onClick: onSmartSchedule,
                }
              : undefined
          }
        />
      ) : (
        <DataTable<ItinerarySummary>
          tableId="inspection-itineraries"
          columns={columns}
          data={rows}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={(row) => navigate(`/inspections/itineraries/${row.uuid}`)}
          rowKey="uuid"
          emptyMessage={t(
            'inspections.itineraries.emptyFilterMessage',
            'No itineraries match this filter.',
          )}
        />
      )}

      {assigning && (
        <AssignItineraryDialog
          open={!!assigning}
          onOpenChange={(open) => {
            if (!open) setAssigning(null)
          }}
          itinerary={assigning}
        />
      )}

      {/* The staged-backlog explainer: what "awaiting itinerary" means and
          exactly which homes are in it. */}
      <Dialog open={backlogOpen} onOpenChange={setBacklogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('inspections.itineraries.backlogDialogTitle', 'Staged, awaiting itinerary')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'inspections.itineraries.backlogDialogDescription',
                "These homes were pulled from the pool into draft inspections (staged) but are not on any trip yet - usually a batch too small to route on its own. Three ways forward: the next Smart Schedule run picks them up; add one to an existing trip's day with Add stop; or open its record and schedule it individually on the console. Remove sends a home back to the pool.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[320px] flex-col gap-1.5 overflow-y-auto py-1">
            {(backlog ?? []).map((row) => (
              <div
                key={row.uuid}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm text-[var(--ink)]">
                    <MapPin
                      size={12}
                      className="shrink-0 text-[var(--ink-3)]"
                    />
                    {[
                      row.property_address_line_1,
                      row.property_city,
                      row.property_state,
                    ]
                      .filter(Boolean)
                      .join(', ') || row.code}
                  </p>
                  <p className="truncate text-[11px] text-[var(--ink-3)]">
                    <Link
                      to={`/inspections/${row.uuid}`}
                      className="font-mono hover:underline"
                    >
                      {row.code}
                    </Link>
                    {row.contractor_name ? ` · ${row.contractor_name}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {row.property_state && (
                    <Badge variant="secondary">{row.property_state}</Badge>
                  )}
                  {canSchedule && (
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      className="text-[var(--ink-3)] hover:text-[var(--status-error,#991b1b)]"
                      disabled={unstage.isPending}
                      aria-label={t(
                        'inspections.itineraries.removeAriaLabel',
                        'Remove {{code}} from the staged backlog',
                        { code: row.code },
                      )}
                      title={t(
                        'inspections.itineraries.removeTitle',
                        'Remove - send back to the pool',
                      )}
                      onClick={async () => {
                        try {
                          await unstage.mutateAsync(row.uuid)
                          toast.success(
                            t(
                              'inspections.itineraries.removeSuccess',
                              'Removed - the home is back in the pool.',
                            ),
                          )
                        } catch (e) {
                          toastApiError(
                            e,
                            t('inspections.itineraries.removeError', 'Could not remove this home.'),
                          )
                        }
                      }}
                    >
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
